import React, { useState, useRef } from "react";
import ReactDOM from "react-dom";
import { X } from "lucide-react";
import { ITEM } from "@/styles/shared";
import { timeAgo } from "@/utils/timeAgo";
import { dueDateStatus } from "@/components/TaskDetailModal";

const PRIORITY_MAP = {
  urgent: { label: "Urgent", color: "#dc2626", bg: "rgba(220,38,38,0.1)",  border: "rgba(220,38,38,0.25)" },
  high:   { label: "High",   color: "#d97706", bg: "rgba(217,119,6,0.1)",  border: "rgba(217,119,6,0.25)" },
  low:    { label: "Low",    color: "#3b82f6", bg: "rgba(59,130,246,0.1)", border: "rgba(59,130,246,0.25)" },
};

function DropItem({ label, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "block", width: "100%", textAlign: "left",
        padding: "0.4rem 0.75rem",
        background: hover ? "rgba(26,77,46,0.06)" : "transparent",
        border: "none", cursor: "pointer",
        fontFamily: "'Manrope', sans-serif", fontSize: "0.8rem", color: "#1A4D2E",
        transition: "background 0.12s",
      }}
    >
      {label}
    </button>
  );
}

export default function TaskRow({ task, fading, isNew, projects, onComplete, onDelete, onAssign, onOpenDetail }) {
  const [deleteHover, setDeleteHover] = useState(false);
  const [checked, setChecked] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0 });
  const assignRef = useRef(null);

  const handleCheck = () => {
    setChecked(true);
    setTimeout(() => onComplete(task.id), 120);
  };

  const handleAssignClick = () => {
    if (!showDropdown && assignRef.current) {
      const rect = assignRef.current.getBoundingClientRect();
      setDropPos({ top: rect.bottom + 4, left: rect.left });
    }
    setShowDropdown((v) => !v);
  };

  const due = dueDateStatus(task.due_date);
  const priBadge = PRIORITY_MAP[task.priority];

  return (
    <div style={{
      overflow: "hidden",
      maxHeight: fading ? "0px" : "200px",
      marginBottom: fading ? "0" : "0.5rem",
      opacity: fading ? 0 : 1,
      transform: fading ? "translateX(18px)" : "translateX(0)",
      transition: "max-height 0.35s ease, margin-bottom 0.35s ease, opacity 0.35s ease, transform 0.35s ease",
    }}>
      <div
        data-testid={`task-row-${task.id}`}
        style={{
          ...ITEM,
          padding: "0.625rem 0.875rem",
          display: "flex", alignItems: "center", gap: "0.75rem",
          animation: isNew ? "taskEnter 0.38s ease-out" : "none",
        }}
      >
        {/* Checkbox */}
        <div
          data-testid={`task-checkbox-${task.id}`}
          onClick={handleCheck}
          style={{
            width: "17px", height: "17px", borderRadius: "50%",
            border: `1.5px solid ${checked ? "#3d6b1f" : "rgba(26,77,46,0.35)"}`,
            background: checked ? "#3d6b1f" : "transparent",
            flexShrink: 0, cursor: "pointer",
            transition: "background 0.15s, border-color 0.15s",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          {checked && (
            <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
              <path d="M1 3.5L3.5 6L8 1" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </div>

        {/* Text */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            data-testid={`task-title-${task.id}`}
            onClick={() => onOpenDetail?.(task)}
            style={{
              fontFamily: "'Manrope', sans-serif", color: "#1A4D2E",
              fontSize: "0.875rem", fontWeight: 500, margin: 0,
              textDecoration: checked ? "line-through" : "none",
              opacity: checked ? 0.5 : 1,
              transition: "opacity 0.2s",
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              cursor: "pointer",
            }}
            onMouseEnter={(e) => { if (!checked) e.currentTarget.style.color = "#3d6b1f"; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = "#1A4D2E"; }}
          >
            {task.title}
          </p>

          {/* Meta row */}
          <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginTop: "0.2rem", flexWrap: "wrap" }}>
            <span style={{ fontFamily: "'Manrope', sans-serif", color: "#6b7280", fontSize: "0.72rem" }}>
              {timeAgo(task.created_at)}
            </span>
            {task.project_title && (
              <span style={{
                fontFamily: "'Manrope', sans-serif", fontSize: "0.68rem", fontWeight: 600,
                background: "rgba(61,107,31,0.1)", color: "#3d6b1f",
                border: "1px solid rgba(61,107,31,0.2)", borderRadius: "0.3rem",
                padding: "0.05rem 0.35rem",
              }}>
                {task.project_emoji} {task.project_title}
              </span>
            )}
            {priBadge && (
              <span style={{
                fontFamily: "'Manrope', sans-serif", fontSize: "0.65rem", fontWeight: 700,
                background: priBadge.bg, color: priBadge.color, border: `1px solid ${priBadge.border}`,
                borderRadius: "0.3rem", padding: "0.05rem 0.35rem", textTransform: "capitalize",
              }}>
                {priBadge.label}
              </span>
            )}
            {due && (
              <span style={{
                fontFamily: "'Manrope', sans-serif", fontSize: "0.65rem", fontWeight: 600,
                background: due.bg, color: due.color, border: `1px solid ${due.border}`,
                borderRadius: "0.3rem", padding: "0.05rem 0.35rem",
              }}>
                {due.label}
              </span>
            )}
          </div>

          {/* Assign to project */}
          {projects && projects.length > 0 && (
            <div style={{ position: "relative", marginTop: "0.2rem" }}>
              <button
                ref={assignRef}
                data-testid={`assign-btn-${task.id}`}
                onClick={handleAssignClick}
                style={{
                  background: "none", border: "none", cursor: "pointer", padding: 0,
                  fontFamily: "'Manrope', sans-serif", fontSize: "0.68rem", color: "#9ca3af",
                  display: "flex", alignItems: "center", gap: "0.15rem",
                }}
              >
                {task.project_title ? "Change project ▾" : "Assign to project ▾"}
              </button>

              {showDropdown && ReactDOM.createPortal(
                <>
                  {/* Backdrop — position: fixed covers whole screen, outside all transforms */}
                  <div
                    style={{ position: "fixed", inset: 0, zIndex: 999 }}
                    onClick={() => setShowDropdown(false)}
                  />
                  {/* Dropdown — rendered at document.body to escape all transform containing blocks */}
                  <div style={{
                    position: "fixed",
                    top: dropPos.top,
                    left: dropPos.left,
                    zIndex: 1000,
                    background: "rgba(255,255,255,0.98)",
                    backdropFilter: "blur(12px)",
                    WebkitBackdropFilter: "blur(12px)",
                    borderRadius: "0.5rem",
                    border: "1px solid rgba(26,77,46,0.18)",
                    boxShadow: "0 8px 28px rgba(26,77,46,0.16)",
                    minWidth: "190px",
                    overflow: "hidden",
                  }}>
                    <DropItem label="— No project" onClick={() => { onAssign(task.id, null); setShowDropdown(false); }} />
                    {projects.map((p) => (
                      <DropItem
                        key={p.id}
                        label={`${p.emoji} ${p.title}`}
                        onClick={() => { onAssign(task.id, p.id); setShowDropdown(false); }}
                      />
                    ))}
                  </div>
                </>,
                document.body
              )}
            </div>
          )}
        </div>

        {/* Delete */}
        <button
          data-testid={`task-delete-${task.id}`}
          onClick={() => onDelete(task.id)}
          onMouseEnter={() => setDeleteHover(true)}
          onMouseLeave={() => setDeleteHover(false)}
          style={{
            background: "none", border: "none", cursor: "pointer",
            color: deleteHover ? "#ef4444" : "#4b5563",
            padding: "0.2rem", display: "flex", alignItems: "center",
            transition: "color 0.15s", flexShrink: 0,
          }}
        >
          <X size={15} />
        </button>
      </div>
    </div>
  );
}
